import { GCInstant as platform } from 'src/lib/gcinstant';
import StateObserver from 'src/StateObserver';
import { PetAppearanceController } from '../appearance/PetAppearanceController';
import { levelActions, LevelState } from '../level/level.redux';
import { EntityManager, fromEntityId, PetEntity } from '../logic/EntityManager';
import SceneManager from '../logic/SceneManager';
import { PetPositionController } from '../position/PetPositionController';
import { Position } from '../position/PositionModel';
import { petActions, PetState } from './pet.redux';
import {
  DialogID,
  DialogScene,
} from 'src/replicant/conversation/conservation.ruleset';
import { SelectedAccessoryState } from 'src/replicant/accessory/accessory.schema';
import {
  DecorationItem,
  DecorationRoomID,
  DecorationSlot,
} from 'src/replicant/decoration/decoration.ruleset';
import * as Decoration from 'src/replicant/decoration';
import { PetConversationController } from '../conversation/PetConversationController';
import { setVisibleStats } from '../../state/ui';
import { evolutionSequence, sleepSequence } from './pet.sequence';
import { ascensionSequence } from '../ascension/ascension.sequence';
import { getMutex } from 'src/replicant/utils/mutex';
import { PetModel } from './PetModel';
import { PlayerPetModel } from './PlayerPetModel';
import { captureGenericError } from 'src/lib/sentry';
import TutorialHand from '../components/shared/TutorialHand';
import { PetState as PetStatus } from 'src/replicant/pet';

export class PetController {
  public readonly id: string;
  public readonly position: PetPositionController;
  public readonly appearance: PetAppearanceController;
  public readonly conversation: PetConversationController;
  private model: PetModel;
  private hand: TutorialHand | undefined;

  public get destroyed() {
    return this._destroyed;
  }

  public get decorations() {
    return this.model.decorations();
  }

  public get features() {
    return this.model.features();
  }

  public get state() {
    return this.model.state();
  }

  public get species() {
    return this.model.species();
  }

  public get stage() {
    return this.model.stage();
  }

  public get isFocused() {
    return StateObserver.getState().ui.visibleStats === this.id;
  }

  public set isFocused(isFocused: boolean) {
    StateObserver.dispatch(
      setVisibleStats(isFocused ? this.id : platform.playerID),
    );
  }

  private evolving = false;
  private _destroyed = false;
  private readonly sequenceMutex = getMutex();

  constructor(
    sceneManager: SceneManager,
    public readonly entity: PetEntity,
  ) {
    this.id = fromEntityId(entity.entityId).id;
    this.model = new PlayerPetModel(); // Use player pet model by default, this will be overriden when initializing.
    this.position = new PetPositionController(this, sceneManager);
    this.appearance = new PetAppearanceController(this);
    this.conversation = new PetConversationController(this);
  }

  public init(
    model: PetModel,
    initialState?: {
      dialog?: DialogID;
      pet?: Partial<PetState>;
      level?: Partial<LevelState>;
      position?: Position;
      accessories?: SelectedAccessoryState[];
    },
  ) {
    this.model = model;
    const { createLevel } = levelActions;
    const { id } = this;

    StateObserver.dispatch(
      petActions.createPet({ id, payload: initialState?.pet ?? {} }),
    );

    StateObserver.dispatch(
      createLevel({
        id,
        payload: initialState?.level ?? {},
      }),
    );

    this.position.init(initialState?.position ?? { scene: '', location: null });
    this.appearance.init(initialState?.accessories ?? []);
    this.conversation.init(initialState?.dialog ?? null);
  }

  /**
   * Hide pet movieclip
   */
  public hide() {
    this.entity.hide();
  }

  /**
   * Show pet movieclip
   */

  public show() {
    this.entity.show();
  }

  public destroy() {
    if (this._destroyed) return;
    this._destroyed = true;
    this.position.setWandering(false);
    this.position.waitForMovement().then(() => {
      this.position.destroy();

      const { id } = this;

      StateObserver.dispatch(petActions.deletePet(id));
      StateObserver.dispatch(levelActions.deleteLevel(id));

      this.conversation.destroy();
      this.appearance.destroy();

      EntityManager.deleteEntity(this.entity.entityId);
    });
  }

  /**
   * Is this current user pet
   */
  public isLocalPet() {
    return this.id === platform.playerID;
  }

  /**
   * Is pet idle
   */
  public isIdle() {
    const { id } = this;
    const { care, pet } = StateObserver.getState();
    return (
      !care.currentCareAction &&
      !pet[id].action &&
      !this.conversation.inProgress()
    );
  }

  public async moveTo(position: Position | null, duration?: number) {
    if (this.destroyed) {
      captureGenericError(
        'Cannot move pet',
        new Error('PetController.moveTo() called on destroyed pet'),
        { level: 'warning' },
      );
      return;
    }

    const currentScene = this.position.position?.scene;
    if (currentScene && position?.scene !== currentScene) {
      this.appearance.setAction('hang');
    }
    await this.position.moveTo(position, duration);

    // Pet could have been destroyed before the move completed.
    if (this.destroyed) return;

    this.appearance.setAction(undefined);
  }

  public isEvolving() {
    return this.evolving;
  }

  public async playSequence(sequence: () => Promise<void>) {
    await this.sequenceMutex.lock();
    try {
      await sequence();
    } finally {
      this.sequenceMutex.release();
    }
  }

  public async evolve() {
    // Already evolving - bail
    if (this.evolving) return;

    // Do not evolve if this is a friend's pet
    if (!this.isLocalPet()) return;

    // Do not evolve if there is no evolving status effect
    if (this.state !== 'evolving') return;

    this.evolving = true;
    try {
      await this.playSequence(() => evolutionSequence(this));
    } finally {
      this.evolving = false;
    }
  }

  public async die() {
    // Do not ascend if this is a friend's pet
    if (!this.isLocalPet()) return;

    // Do not ascend if there is no dead status effect
    if (this.state !== 'dead') return;

    await this.playSequence(() => ascensionSequence(this));
  }

  public async goToSleep() {
    // Already in bed - bail
    if (this.position.position?.scene === 'bedroom') return;

    // Do not sleep if this is a friend's pet
    if (!this.isLocalPet()) return;

    // Do not sleep if there is no asleep status effect
    if (this.state !== 'sleeping') return;

    await this.playSequence(() => sleepSequence(this));
  }

  /**
   * Start conversation for current pet
   */
  public async startConversation(scene: DialogScene = 'normal_chat') {
    if (!this.species) return;

    return this.conversation.begin(scene, this.species, this.state);
  }

  /**
   * Get room decoration for current pet
   * It may be a friend pet in this case you will get friends decorations for room
   */
  public getRoomDecoration(
    room: DecorationRoomID,
  ): Map<DecorationSlot, DecorationItem> {
    return Decoration.getters.getSelectedRoomDecorations(
      this.decorations,
      room,
    );
  }

  public setEvolutionEffectsVisible(visible: boolean) {
    return this.entity.setEvolutionEffectsVisible(visible);
  }

  public showTutorialHand() {
    if (!this.hand) {
      this.hand = new TutorialHand({
        superview: this.entity,
        visible: false,
      });
    }
    this.hand.fadeIn(250, 200, false);
  }

  public hideTutorialHand() {
    this.hand?.fadeOut();
  }
}
